import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from "chart.js";
import { Bar } from "react-chartjs-2";
import { Button, Row, Col } from "../index";
import { getChartColour, initialiseDatasets, updateChartColours, setClickedColourToSolid, setBarClickedData, generateBarLabels, updateOpacitys, resetItemClickedData, downloadItem, } from "./utils";
import { isPropDefined, getCssVarValue } from "../general/utils";
import { themeListener } from "../hooks/listeners";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
function BarChart(props) {
    const { id, title, labels, setBarData, cumulative } = props;
    const height = props.height !== undefined ? props.height : "100%";
    const stacked = isPropDefined(props.stacked);
    const originDatasets = initialiseDatasets(props.datasets);
    const [datasets, setDatasets] = useState(originDatasets);
    const [prevOrder, setPrevOrder] = useState(null);
    const [prevLegendItemIndex, setPrevLegendItemIndex] = useState(null);
    // Used to change the height of the y-axis when selecting a legend
    const [maxHeight, setMaxHeight] = useState(null);
    // colours
    const [titleColour, setTitleColour] = useState("");
    const [labelColour, setLabelColour] = useState("");
    const [gridColour, setGridColour] = useState("");
    themeListener(() => {
        setTitleColour(getCssVarValue("--tol-emphasis"));
        setLabelColour(getCssVarValue("--tol-text"));
        setGridColour(getCssVarValue("--tol-grey"));
    });
    //making sure legendclick is disabled when cumulative toggle is on
    const isInteractive = !cumulative && isPropDefined(setBarData);
    // functions for options
    function handleLegendClick(event, legendItem, legend) {
        if (isInteractive) {
            const legendIndex = event.chart.data.datasets.findIndex((obj) => obj.label === legendItem.text);
            let selectedBucket = null;
            // cannot keep clicking on the same legend item
            if (prevLegendItemIndex !== legendIndex) {
                legend.chart.data.datasets.forEach((dataset, index) => {
                    if (index === legendIndex) {
                        dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, "1");
                        setPrevOrder(dataset.order);
                        setPrevLegendItemIndex(index);
                        dataset.order = -1;
                        selectedBucket = dataset.id;
                        const maxValue = Math.max(...dataset.data);
                        const maxValuePercentage = Math.ceil(maxValue * 1.1);
                        setMaxHeight(maxValuePercentage);
                    }
                    else {
                        dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, "0.25");
                        // reset prev item's order
                        if (prevLegendItemIndex === index) {
                            dataset.order = prevOrder;
                        }
                    }
                });
                // sets the bar data to the selected legend
                setBarData({
                    bucket: selectedBucket,
                    value: null,
                    clickKey: null,
                });
            }
            else {
                setMaxHeight(null);
                legend.chart.data.datasets.forEach((dataset, index) => {
                    dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, "1");
                    setPrevOrder(null);
                    setPrevLegendItemIndex(null);
                    dataset.order = index;
                });
                // sets the bar data to the selected legend
                setBarData({});
            }
            legend.chart.update();
            setDatasets(legend.chart.data.datasets);
        }
    }
    function handleLegendHover(event) {
        if (isPropDefined(setBarData)) {
            event.native.target.style.cursor = "pointer";
        }
    }
    function handlePlaneClick(
    // @ts-ignore
    event, chartElement, chart, item) {
        setMaxHeight(null);
        if (item !== undefined) {
            return;
        }
        // reset order on 'plane reset click'
        if (prevLegendItemIndex !== null) {
            chart.data.datasets[prevLegendItemIndex].order = prevOrder;
            setPrevOrder(null);
            setPrevLegendItemIndex(null);
        }
        // only clickable if setBarData is defined
        if (isPropDefined(setBarData)) {
            if (!chartElement.length) {
                // reset bar colours when clicking any other part of chart
                updateChartColours(chart, true, 0.5);
                resetItemClickedData(setBarData);
            }
            else {
                // fade non-clicked bars
                updateChartColours(chart, false, 0.25);
                // setting clicked bar as its original colour
                setClickedColourToSolid(chart, chartElement);
                setBarClickedData(chart, chartElement, setBarData);
            }
            chart.update();
        }
        setDatasets(chart.data.datasets);
    }
    function handlePlaneHover(event, chartElement) {
        if (isPropDefined(setBarData)) {
            event.native.target.style.cursor = chartElement[0]
                ? "pointer"
                : "default";
        }
    }
    // chart options
    const options = {
        animation: false,
        maintainAspectRatio: false,
        responsive: true,
        devicePixelRatio: 2,
        plugins: {
            title: {},
            tooltip: {
                animation: false,
                usePointStyle: true,
                backgroundColor: "black",
                callbacks: {
                    labelPointStyle: () => {
                        return {
                            pointStyle: "rectRounded",
                            rotation: 0,
                        };
                    },
                    labelColor: (context) => {
                        const colour = getChartColour(context.datasetIndex);
                        return {
                            backgroundColor: colour,
                            borderColor: colour,
                        };
                    },
                },
            },
            legend: {
                onHover: handleLegendHover,
                onClick: handleLegendClick,
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    generateLabels: (chart) => {
                        return generateBarLabels(chart, titleColour);
                    },
                },
            },
        },
        layout: {
            padding: {
                left: 10,
                right: 10,
                bottom: 10,
            },
        },
        onClick: handlePlaneClick,
        onHover: handlePlaneHover,
        scales: {
            x: {
                stacked: stacked,
                grid: {
                    display: false,
                },
                ticks: {
                    // x labels
                    color: labelColour,
                },
            },
            y: {
                stacked: stacked,
                max: maxHeight,
                grid: {
                    color: gridColour,
                },
                ticks: {
                    // y labels
                    color: labelColour,
                },
            },
        },
    };
    const downloadName = props.downloadName !== undefined ? props.downloadName : "barchart";
    return (_jsxs("div", { style: { height: height, paddingBottom: "20px" }, children: [_jsxs(Row, { children: [_jsx(Col, { xs: 6, children: _jsx("div", { className: "header-text", children: title }) }), _jsx(Col, { xs: 6, children: _jsxs("div", { className: "tol-chart-buttons", children: [isPropDefined(setBarData) && (_jsx(Button, { outline: true, position: "right", type: "primary", onClick: () => {
                                        resetItemClickedData(setBarData);
                                        setMaxHeight(null);
                                        setDatasets(originDatasets);
                                    }, icon: "undo" })), _jsx(Button, { outline: true, position: "right", type: "primary", onClick: () => {
                                        downloadItem(props.id, downloadName);
                                    }, icon: "download" })] }) })] }), _jsx(Bar, { id: id, responsive: "true", className: "tol-bar-chart", datasetIdKey: "id", 
                // @ts-ignore
                options: options, data: {
                    labels: labels,
                    datasets: datasets,
                } })] }));
}
export default BarChart;
